import React, { useState, useEffect } from 'react';
import { getLocations, getEmployees, getTemplates, addTemplate, deleteTemplateByDetails, updateTemplate } from '../api';

function ScheduleTemplates() {
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDay, setModalDay] = useState(null);
  const [timeModalOpen, setTimeModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [timeInputs, setTimeInputs] = useState({ start_time: '', end_time: '' });
  const [employeeSearch, setEmployeeSearch] = useState('');
  // Get tenant_id from logged-in user
const user = JSON.parse(localStorage.getItem('user') || '{}');
const tenantId = user.tenant_id || 1;

  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [locRes, empRes, tempRes] = await Promise.all([
        getLocations(tenantId),
        getEmployees(tenantId),
        getTemplates(tenantId)
      ]);
      setLocations(locRes.data.filter(l => l.active));
      setEmployees(empRes.data.filter(e => e.active));
      setTemplates(tempRes.data);
      
      if (locRes.data.length > 0 && !selectedLocation) {
        setSelectedLocation(locRes.data.filter(l => l.active)[0]?.id);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTemplatesForDay = (locationId, dayIndex) => {
    return templates.filter(t => t.location_id === locationId && t.day_of_week === dayIndex);
  };

  const isEmployeeInTemplate = (employeeId, locationId, dayIndex) => {
    return templates.some(t => 
      t.employee_id === employeeId && 
      t.location_id === locationId && 
      t.day_of_week === dayIndex
    );
  };

  const handleToggleEmployee = async (employeeId, dayIndex) => {
    if (!selectedLocation) return;

    const exists = isEmployeeInTemplate(employeeId, selectedLocation, dayIndex);

    try {
      if (exists) {
        await deleteTemplateByDetails(selectedLocation, employeeId, dayIndex);
      } else {
        await addTemplate({
          tenant_id: tenantId,
          location_id: selectedLocation,
          employee_id: employeeId,
          day_of_week: dayIndex,
          start_time: null,
          end_time: null
        });
      }
      fetchData();
    } catch (err) {
      console.error('Failed to update template:', err);
      alert('Failed to update template');
    }
  };

  const openModal = (dayIndex) => {
    setModalDay(dayIndex);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalDay(null);
    setEmployeeSearch('');
  };

  const openTimeModal = (template) => {
    setSelectedTemplate(template);
    setTimeInputs({
      start_time: template.start_time ? template.start_time.slice(0, 5) : '',
      end_time: template.end_time ? template.end_time.slice(0, 5) : ''
    });
    setTimeModalOpen(true);
  };

  const closeTimeModal = () => {
    setTimeModalOpen(false);
    setSelectedTemplate(null);
    setTimeInputs({ start_time: '', end_time: '' });
  };

  const handleSaveTime = async () => {
    if (!selectedTemplate) return;

    try {
      await updateTemplate(selectedTemplate.id, {
        start_time: timeInputs.start_time || null,
        end_time: timeInputs.end_time || null
      });
      await fetchData();
      closeTimeModal();
    } catch (err) {
      console.error('Failed to update time:', err);
      alert('Failed to update time');
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes}${ampm}`;
  };

  const currentLocation = locations.find(l => l.id === selectedLocation);

  if (loading) {
    return (
      <div style={{ padding: '48px' }}>
        <p className="text-gray-600">Loading templates...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '48px' }}>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Schedule Templates</h1>
        <p className="text-gray-600 mt-1">Set up the regular crew for each location</p>
      </div>

      {/* Location Selector */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: '12px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        overflowX: 'auto'
      }}>
        {locations.map((loc) => (
          <button
            key={loc.id}
            onClick={() => setSelectedLocation(loc.id)}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: selectedLocation === loc.id ? 'none' : '1px solid #e5e7eb',
              cursor: 'pointer',
              backgroundColor: selectedLocation === loc.id ? '#3b82f6' : '#ffffff',
              color: selectedLocation === loc.id ? '#ffffff' : '#374151',
              fontWeight: '500',
              fontSize: '14px',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
          >
            {loc.name}
          </button>
        ))}
      </div>

      {/* Template Grid */}
      {currentLocation && (
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '24px'
        }}>
          <h2 className="text-xl font-bold text-gray-900 mb-4">{currentLocation.name}</h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '12px'
          }}>
            {days.map((day, index) => {
              const dayTemplates = getTemplatesForDay(selectedLocation, index);
              return (
                <div 
                  key={day}
                  style={{
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    padding: '16px',
                    minHeight: '200px'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}>
                    <h3 className="font-semibold text-gray-900">{day}</h3>
                    <span style={{
                      backgroundColor: dayTemplates.length > 0 ? '#dbeafe' : '#f3f4f6',
                      color: dayTemplates.length > 0 ? '#1d4ed8' : '#6b7280',
                      fontSize: '12px',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontWeight: '600'
                    }}>
                      {dayTemplates.length}
                    </span>
                  </div>

                  {/* Employee list for this day */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
                    {dayTemplates.map((template) => (
                      <div 
                        key={template.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          backgroundColor: '#ffffff',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          fontSize: '13px'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', color: '#374151' }}>
                            {template.first_name} {template.last_name?.charAt(0)}.
                          </div>
                          <button
                            onClick={() => openTimeModal(template)}
                            style={{
                              fontSize: '11px',
                              color: template.start_time ? '#059669' : '#9ca3af',
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              cursor: 'pointer',
                              textAlign: 'left'
                            }}
                          >
                            {template.start_time ? formatTime(template.start_time) : 'Set time'}
                          </button>
                        </div>
                        <button
                          onClick={() => handleToggleEmployee(template.employee_id, index)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#ef4444',
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: '0 4px'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Add button */}
                  <button
                    onClick={() => openModal(index)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      backgroundColor: '#ffffff',
                      border: '2px dashed #d1d5db',
                      borderRadius: '6px',
                      color: '#6b7280',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      transition: 'all 0.2s'
                    }}
                  >
                    + Add
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add Employee Modal */}
      {modalOpen && modalDay !== null && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            width: '400px',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h3 className="text-lg font-bold text-gray-900">
                Add Employees - {days[modalDay]}
              </h3>
              <button
                onClick={closeModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Tap an employee to add/remove them from {days[modalDay]}
            </p>

            <input
              type="text"
              placeholder="Search employees..."
              value={employeeSearch}
              onChange={(e) => setEmployeeSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '14px',
                borderRadius: '8px',
                border: '2px solid #e5e7eb',
                marginBottom: '12px',
                boxSizing: 'border-box'
              }}
            />

            <div style={{ 
              overflowY: 'auto',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              {employees
                .filter(emp => 
                  `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(employeeSearch.toLowerCase())
                )
                .map((emp) => {
                const isSelected = isEmployeeInTemplate(emp.id, selectedLocation, modalDay);
                return (
                  <button
                    key={emp.id}
                    onClick={() => handleToggleEmployee(emp.id, modalDay)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      backgroundColor: isSelected ? '#dbeafe' : '#f9fafb',
                      border: isSelected ? '2px solid #3b82f6' : '2px solid transparent',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'left'
                    }}
                  >
                    <div>
                      <span style={{ 
                        fontWeight: '500',
                        color: isSelected ? '#1d4ed8' : '#374151'
                      }}>
                        {emp.first_name} {emp.last_name}
                      </span>
                      <span style={{
                        marginLeft: '8px',
                        fontSize: '12px',
                        color: '#6b7280'
                      }}>
                        Lvl {emp.seniority_level}
                      </span>
                    </div>
                    {isSelected && (
                      <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>✓</span>
                    )}
                  </button>
                );
              })}
            </div>

            <button
              onClick={closeModal}
              style={{
                marginTop: '16px',
                width: '100%',
                padding: '12px',
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Time Edit Modal */}
      {timeModalOpen && selectedTemplate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            width: '320px'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h3 className="text-lg font-bold text-gray-900">
                Set Time
              </h3>
              <button
                onClick={closeTimeModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              {selectedTemplate.first_name} {selectedTemplate.last_name} - {days[selectedTemplate.day_of_week]}
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                Start Time
              </label>
              <select
                value={timeInputs.start_time}
                onChange={(e) => setTimeInputs({ ...timeInputs, start_time: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  boxSizing: 'border-box',
                  backgroundColor: '#ffffff'
                }}
              >
                <option value="">Select time</option>
                {Array.from({ length: 24 }, (_, hour) => (
                  <>
                    <option key={`${hour}:00`} value={`${hour.toString().padStart(2, '0')}:00`}>
                      {hour === 0 ? '12:00 AM' : hour < 12 ? `${hour}:00 AM` : hour === 12 ? '12:00 PM' : `${hour - 12}:00 PM`}
                    </option>
                    <option key={`${hour}:30`} value={`${hour.toString().padStart(2, '0')}:30`}>
                      {hour === 0 ? '12:30 AM' : hour < 12 ? `${hour}:30 AM` : hour === 12 ? '12:30 PM' : `${hour - 12}:30 PM`}
                    </option>
                  </>
                )).flat()}
              </select>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                End Time <span style={{ fontWeight: '400', color: '#9ca3af' }}>(optional)</span>
              </label>
              <select
                value={timeInputs.end_time}
                onChange={(e) => setTimeInputs({ ...timeInputs, end_time: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  boxSizing: 'border-box',
                  backgroundColor: '#ffffff'
                }}
              >
                <option value="">Select time</option>
                {Array.from({ length: 24 }, (_, hour) => (
                  <>
                    <option key={`${hour}:00`} value={`${hour.toString().padStart(2, '0')}:00`}>
                      {hour === 0 ? '12:00 AM' : hour < 12 ? `${hour}:00 AM` : hour === 12 ? '12:00 PM' : `${hour - 12}:00 PM`}
                    </option>
                    <option key={`${hour}:30`} value={`${hour.toString().padStart(2, '0')}:30`}>
                      {hour === 0 ? '12:30 AM' : hour < 12 ? `${hour}:30 AM` : hour === 12 ? '12:30 PM' : `${hour - 12}:30 PM`}
                    </option>
                  </>
                )).flat()}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setTimeInputs({ start_time: '', end_time: '' });
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                Clear
              </button>
              <button
                onClick={handleSaveTime}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div style={{
        marginTop: '24px',
        backgroundColor: '#f0f9ff',
        border: '1px solid #bae6fd',
        borderRadius: '8px',
        padding: '16px'
      }}>
        <h3 className="font-semibold text-blue-800 mb-2">How to use</h3>
        <ul className="text-sm text-blue-700" style={{ paddingLeft: '20px' }}>
          <li style={{ marginBottom: '4px' }}>Select a location using the buttons above</li>
          <li style={{ marginBottom: '4px' }}>Click "+ Add" on any day to add employees to that day's regular crew</li>
          <li style={{ marginBottom: '4px' }}>Click the time below a name to set their start/end time</li>
          <li style={{ marginBottom: '4px' }}>Click the × next to a name to remove them</li>
          <li>These templates will auto-fill when you create a new weekly schedule</li>
        </ul>
      </div>
    </div>
  );
}

export default ScheduleTemplates;